import { createContext, useContext, useEffect, useRef, type RefObject } from 'react';

/**
 * Closes a floating layer (popover, listbox, menu) on Escape, on a press outside it,
 * and optionally when focus moves outside it.
 *
 * Layers nest by the REACT tree, not by DOM position or mount order: a component
 * that renders a layer wraps its content in `<DismissLayerProvider value={layer}>`
 * (Popover does this), and any layer opened inside becomes its child. Then:
 *
 * - a press inside a child layer counts as inside the parent too, even though the
 *   child's panel is portalled to <body> and sits outside the parent in the DOM;
 * - Escape closes only the innermost open layer (the most recently opened one when
 *   several unrelated layers are open).
 *
 * Escape is caught on `window` in the capture phase and stopped there, so a dialog
 * underneath that listens on `document` never sees the key a popover already used.
 */

export type DismissReason = 'escape' | 'outside' | 'focus';

export interface UseDismissOptions {
  /** Listen only while true; normally the layer's `open`. */
  enabled: boolean;
  /** Everything that counts as inside: the trigger/anchor and the floating panel. */
  refs: ReadonlyArray<RefObject<HTMLElement | null>>;
  onDismiss: (reason: DismissReason, event: Event) => void;
  /** Close on Escape. Default true. */
  escapeKey?: boolean;
  /** Close on a pointer press outside every ref. Default true. */
  outsidePress?: boolean;
  /** Close when focus lands outside every ref (Tab away). Default false. */
  focusOutside?: boolean;
}

export interface DismissLayer {
  readonly children: Set<DismissLayer>;
  active: boolean;
  activatedAt: number;
  ownContains: (node: Node) => boolean;
}

const DismissLayerContext = createContext<DismissLayer | null>(null);

/** Wrap a layer's content so layers opened inside it nest under it. */
export const DismissLayerProvider = DismissLayerContext.Provider;

const activeLayers = new Set<DismissLayer>();
let clock = 0;

function hasOpenChild(layer: DismissLayer): boolean {
  for (const child of layer.children) if (child.active) return true;
  return false;
}

/** Inside this layer or any open layer nested in it. */
function treeContains(layer: DismissLayer, node: Node): boolean {
  if (layer.ownContains(node)) return true;
  for (const child of layer.children) {
    if (child.active && treeContains(child, node)) return true;
  }
  return false;
}

/** The innermost open layer; among unrelated ones, the most recently opened. */
function isTopLayer(layer: DismissLayer): boolean {
  if (hasOpenChild(layer)) return false;
  let latest: DismissLayer | null = null;
  for (const candidate of activeLayers) {
    if (hasOpenChild(candidate)) continue;
    if (!latest || candidate.activatedAt > latest.activatedAt) latest = candidate;
  }
  return latest === layer;
}

export function useDismiss({
  enabled,
  refs,
  onDismiss,
  escapeKey = true,
  outsidePress = true,
  focusOutside = false,
}: UseDismissOptions): DismissLayer {
  const parent = useContext(DismissLayerContext);

  // Latest-value refs: the listeners below are attached once per open, but must
  // always see the current refs and callback.
  const refsRef = useRef(refs);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    refsRef.current = refs;
    onDismissRef.current = onDismiss;
  });

  const layerRef = useRef<DismissLayer | null>(null);
  if (layerRef.current === null) {
    layerRef.current = {
      children: new Set(),
      active: false,
      activatedAt: 0,
      ownContains: (node) => refsRef.current.some((r) => !!r.current && r.current.contains(node)),
    };
  }
  const layer = layerRef.current;

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return undefined;

    layer.active = true;
    clock += 1;
    layer.activatedAt = clock;
    activeLayers.add(layer);
    parent?.children.add(layer);

    const onKeyDown = (event: KeyboardEvent) => {
      if (!escapeKey || event.key !== 'Escape' || event.isComposing) return;
      if (!isTopLayer(layer)) return;
      event.preventDefault();
      event.stopPropagation();
      onDismissRef.current('escape', event);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!outsidePress) return;
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const target = (path[0] ?? event.target) as EventTarget | null;
      if (!(target instanceof Node) || !target.isConnected) return;
      if (treeContains(layer, target)) return;
      onDismissRef.current('outside', event);
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!focusOutside) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (treeContains(layer, target)) return;
      onDismissRef.current('focus', event);
    };

    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn);
      layer.active = false;
      activeLayers.delete(layer);
      parent?.children.delete(layer);
    };
  }, [enabled, escapeKey, outsidePress, focusOutside, layer, parent]);

  return layer;
}

export default useDismiss;
